import { faEdit } from '@fortawesome/free-regular-svg-icons'
import { TFunction } from 'i18next'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import Authorization from '../../../gateway/Authentication/Authorization'
import DataTable, { CaptionButton } from '../../../gateway/components/DataTable'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import Loading from '../../../gateway/layout/Loading'
import GatewayType from '../../../gateway/pages/GatewayType'
import O365SettingsService from '../service/O365SettingsService'

const O365Settings: GatewayType = () => {
  const { t } = useTranslation()
  const [o365Setting, setO365Setting] = useState({} as any)
  const [loading, setLoading] = useState(true)
  const { editDialogPropsSetter } = useContext(DialogContext)

  useEffect(() => {
    O365SettingsService.getO365Settings()
      .then((o365Setting) => setO365Setting(o365Setting))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        text: 'label',
        dataField: 'label',
        classes: 'font-weight-bolder',
        sort: true,
      },
      {
        text: 'value',
        dataField: 'value',
        sort: true,
      },
    ]
  }, [])

  const data = [
    { label: t('o365Setting.gatedHostName'), value: o365Setting.gatedHostName },
    { label: t('o365Setting.exchangeHostName'), value: o365Setting.exchangeHostName },
    { label: t('o365Setting.ewsDefaultUser'), value: o365Setting.ewsDefaultUser },
    { label: t('o365Setting.ewsUrl'), value: o365Setting.ewsUrl },
    { label: t('o365Setting.clientId'), value: o365Setting.clientId },
    { label: t('o365Setting.clientSecret'), value: o365Setting.clientSecret ? '........' : '' },
    { label: t('o365Setting.tenantId'), value: o365Setting.tenantId },
    { label: t('o365Setting.internalDomains'), value: o365Setting.internalDomains?.join(', ') },
    { label: t('o365Setting.sharePointTenantDNS'), value: o365Setting.sharePointTenantDNS },
    { label: t('o365Setting.oneDriveTenantDNS'), value: o365Setting.oneDriveTenantDNS },
  ]

  var captionButtons: CaptionButton[] = [
    {
      onClick: () => configureO365BaseSettingsDialog(t, editDialogPropsSetter, setO365Setting, o365Setting),
      text: t('o365Setting.configureBase'),
      icon: faEdit,
      permissions: Authorization.settings.o365Setting.edit,
    },
  ]

  if (Object.keys(o365Setting).length > 0 && o365Setting.clientId) {
    captionButtons.push({
      onClick: () => configureSpAndOdSettingsDialog(t, editDialogPropsSetter, setO365Setting, o365Setting),
      text: t('o365Setting.configureSpAndOd'),
      icon: faEdit,
      permissions: Authorization.settings.o365Setting.edit,
    })
  }

  return (
    <Loading isLoading={loading}>
      <DataTable
        keyField='label'
        data={data}
        columns={columns}
        headerClasses='d-none'
        searchable={false}
        caption={t('o365Setting.text')}
        captionButtons={captionButtons}
      />
    </Loading>
  )
}

function configureO365BaseSettingsDialog(t: TFunction, editDialogPropsSetter: any, setO365Setting: React.Dispatch<any>, o365Setting: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('o365Setting.configureHeader'),
    onSubmit: O365SettingsService.configureO365BaseSettings,
    onSubmitExtraArgs: [t, setO365Setting],
    labelGridSize: 5,
    data: [
      {
        id: 'gatedHostName',
        value: o365Setting.gatedHostName,
        type: 'text',
        label: t('gatedHostName.label'),
        placeholder: t('gatedHostName.label'),
        autoComplete: 'gatedHostName',
        required: true,
        requiredMessage: t('gatedHostName.inValidMessage'),
      },
      {
        id: 'exchangeHostName',
        value: o365Setting.exchangeHostName,
        type: 'text',
        label: t('o365Setting.exchangeHostName'),
        placeholder: t('o365Setting.exchangeHostNamePlaceHolder'),
        autoComplete: 'exchangeHostName',
      },
      {
        id: 'ewsDefaultUser',
        value: o365Setting.ewsDefaultUser,
        type: 'text',
        label: t('ewsDefaultUser.label'),
        placeholder: t('ewsDefaultUser.label'),
        autoComplete: 'ewsDefaultUser',
        required: true,
        requiredMessage: t('ewsDefaultUser.inValidMessage'),
      },
      {
        id: 'ewsUrl',
        value: o365Setting.ewsUrl,
        type: 'text',
        label: t('ewsUrl.label'),
        placeholder: t('ewsUrl.label'),
        autoComplete: 'ewsUrl',
        required: true,
        requiredMessage: t('ewsUrl.inValidMessage'),
      },
      {
        id: 'clientId',
        value: o365Setting.clientId,
        type: 'text',
        label: t('clientId.label'),
        placeholder: t('clientId.label'),
        autoComplete: 'clientId',
        required: true,
        requiredMessage: t('clientId.inValidMessage'),
      },
      {
        id: 'clientSecret',
        value: o365Setting.clientSecret,
        type: 'password',
        label: t('clientSecret.label'),
        placeholder: t('clientSecret.label'),
        autoComplete: 'new-Password',
        required: true,
        requiredMessage: t('clientSecret.inValidMessage'),
      },
      {
        id: 'tenantId',
        value: o365Setting.tenantId,
        type: 'text',
        label: t('tenantId.label'),
        placeholder: t('tenantId.label'),
        autoComplete: 'tenantId',
        required: true,
        requiredMessage: t('tenantId.inValidMessage'),
      },
      {
        id: 'internalDomains',
        value: o365Setting.internalDomains?.join(', '),
        type: 'textarea',
        label: t('internalDomains.label'),
        placeholder: t('internalDomains.placeHolder'),
        autoComplete: 'internalDomains',
        required: true,
        requiredMessage: t('internalDomains.inValidMessage'),
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function configureSpAndOdSettingsDialog(t: TFunction, editDialogPropsSetter: any, setO365Setting: React.Dispatch<any>, o365Setting: any) {
  const editDialogProps: EditDialogProps = {
    show: true,
    header: t('o365Setting.configureHeader'),
    onSubmit: O365SettingsService.configureSpAndOdSettings,
    onSubmitExtraArgs: [t, setO365Setting],
    labelGridSize: 5,
    data: [
      {
        id: 'sharePointTenantDNS',
        value: o365Setting.sharePointTenantDNS,
        type: 'text',
        label: t('o365Setting.sharePointTenantDNS'),
        placeholder: t('o365Setting.sharePointTenantDNS'),
        autoComplete: 'sharePointTenantDNS',
      },
      {
        id: 'oneDriveTenantDNS',
        value: o365Setting.oneDriveTenantDNS,
        type: 'text',
        label: t('o365Setting.oneDriveTenantDNS'),
        placeholder: t('o365Setting.oneDriveTenantDNS'),
        autoComplete: 'oneDriveTenantDNS',
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

export default O365Settings
